import { COVERAGE_LABEL_FIELD } from "./types";

import type { CompsGridRow } from "./types";
import type { GridApi, GridState } from "ag-grid-community";

/** The shared, per-RFP hidden state persisted to the comps-visibility store. */
export interface CompsVisibilityState {
  /**
   * False until a hidden set has been persisted for the RFP. Distinguishes
   * "nobody has configured this" — where the defaults below apply — from "the
   * analyst unhid everything", which empty lists alone cannot express.
   */
  configured: boolean;
  /** Coverage `rowGroupId`s: a `planGroupId`, or a `subsidy:<kind>` sentinel. */
  hiddenRowGroupIds: string[];
  /** Column ids: a carrier id, or the `requested` / `incumbent` pseudo-columns. */
  hiddenColumnIds: string[];
}

type CompsVisibilityGridState = Pick<GridState, "columnVisibility">;

/**
 * Projects the stored hidden columns onto the AG-Grid state facet that
 * expresses them. Hidden *rows* are deliberately absent: they drive a custom
 * external filter rather than any native grid state, so they travel as a prop
 * instead.
 */
export const toCompsVisibilityGridState = (
  hiddenColumnIds: string[],
): CompsVisibilityGridState => {
  // The coverage column is pinned and has no toggle in the Carriers panel, so
  // drop it even if a stale store entry names it — hiding the grid's label
  // column would leave every row unidentifiable.
  const hiddenColIds = hiddenColumnIds.filter(
    (id) => id !== COVERAGE_LABEL_FIELD,
  );

  return {
    columnVisibility: hiddenColIds.length > 0 ? { hiddenColIds } : undefined,
  };
};

/** Reads the currently hidden columns back out of the live grid. */
export const deriveCompsHiddenColumnIds = (
  api: GridApi<CompsGridRow>,
): string[] =>
  (api.getState().columnVisibility?.hiddenColIds ?? []).filter(
    (id) => id !== COVERAGE_LABEL_FIELD,
  );

const sameMembers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);

  return a.every((value) => bSet.has(value));
};

/** Order-insensitive equality for two visibility states. */
export const compsVisibilityStatesEqual = (
  a: CompsVisibilityState,
  b: CompsVisibilityState,
): boolean =>
  a.configured === b.configured &&
  sameMembers(a.hiddenRowGroupIds, b.hiddenRowGroupIds) &&
  sameMembers(a.hiddenColumnIds, b.hiddenColumnIds);

const SUBSIDY_ROW_META_KEYS: ReadonlySet<string> = new Set([
  "rowId",
  "rowGroupId",
  "planGroupId",
  "rowType",
  "subsidyKind",
  "coverageLabel",
]);

// A subsidy row has data when any of its column cells holds a non-empty value.
// Empty carrier cells are dropped upstream; the always-present Requested/Incumbent
// column keys sit at empty string on a data-less row, so both read as "no data".
const subsidyRowHasData = (row: CompsGridRow): boolean =>
  Object.entries(row).some(
    ([key, value]) =>
      !SUBSIDY_ROW_META_KEYS.has(key) &&
      typeof value === "string" &&
      value.length > 0,
  );

/**
 * The hidden rows an unconfigured RFP starts with: Implementation Credits and
 * Wellness Dollars always hidden, Technology Subsidy hidden only when it has no
 * data, so a carrier's or analyst's tech-subsidy content surfaces on its own.
 *
 * Computed once per RFP and then persisted, so later loads read server truth
 * rather than re-deriving — otherwise unhiding Wellness Dollars would not stick.
 */
export const defaultHiddenRowGroupIds = (rowData: CompsGridRow[]): string[] =>
  rowData
    .filter(
      (row) =>
        row.rowType === "subsidy" &&
        (row.subsidyKind !== "techCredit" || !subsidyRowHasData(row)),
    )
    .map((row) => row.rowGroupId);
